import type { Format } from './syntaxHighlight';

interface FormatResult {
  /** 格式化后的文本 */
  formatted: string;
  /** 格式化失败时的错误消息 */
  error?: string;
}

/**
 * 按格式类型格式化文本
 * 仅在 autoFormat=true 时由 TextDiff 组件调用
 */
export function formatByType(text: string, format: Format): FormatResult {
  if (!text.trim()) return { formatted: text };

  switch (format) {
    case 'json':
      return formatJson(text);
    case 'xml':
    case 'html':
      return formatXml(text);
    default:
      // 其他格式不做自动格式化
      return { formatted: text };
  }
}

/** JSON 格式化：parse → stringify 往返 */
function formatJson(text: string): FormatResult {
  try {
    const parsed = JSON.parse(text);
    return { formatted: JSON.stringify(parsed, null, 2) };
  } catch (e) {
    const msg = e instanceof SyntaxError ? e.message : String(e);
    return { formatted: text, error: `JSON 格式错误: ${msg}` };
  }
}

/** XML/HTML 简单缩进美化 */
function formatXml(text: string): FormatResult {
  try {
    // 移除标签间空白，然后按层级缩进
    let indent = 0;
    const lines: string[] = [];

    // 简单按 < 和 > 分割并重建
    const tokens = text
      .replace(/></g, '>|<')
      .split(/(?=<)|(?<=>)/g)
      .filter((t) => t.trim());

    for (const token of tokens) {
      const trimmed = token.trim();
      if (!trimmed) continue;

      // 闭合标签或自闭合标签 → 先减少缩进
      if (/^<\/|^\/>/.test(trimmed)) {
        indent = Math.max(0, indent - 1);
      }

      lines.push('  '.repeat(indent) + trimmed);

      // 开始标签（非自闭合）→ 增加缩进
      if (/^<[a-zA-Z][^/]*(?<!\\)>$/.test(trimmed) && !/\/>$/.test(trimmed)) {
        indent++;
      }
    }

    return { formatted: lines.join('\n') };
  } catch {
    return { formatted: text };
  }
}
